/**
 * Canonical Zoom-account options shown in the admin class-codes UI.
 *
 * The DB stores the chosen value as a free-form string in the `notes` field
 * of ClassJoinCode and each ISessionOverride; this module is the single
 * boundary where that string is interpreted as a structured value.
 *
 * Conflict-checking rule:
 *  - SHARED accounts (Account 1, Account 2, ...) are real shared resources;
 *    same shared account at overlapping times = double-booking.
 *  - PERSONAL means the staff is on their own personal Zoom account, which
 *    is per-staff and inherently unique. Never produces conflicts.
 *  - OTHER (custom text) is an escape hatch for future-proofing. Since we
 *    don't know what it actually is, it never blocks save.
 *
 * This is the account catalog, not the active-account list. Class selectors
 * offer only accounts with complete stored S2S credentials, fetched from the
 * server. Parsing and conflict checks keep recognizing existing assignments
 * even after credentials are removed. To add another account, append it here
 * and connect its credentials (docs/zoom-s2s-integration.md).
 */

/** Shared accounts that participate in conflict checking. */
export const SHARED_ZOOM_ACCOUNTS = [
  'Account 1',
  'Account 2',
  'Account 3',
  'Account 4',
  'Account 5',
  'Account 6',
  'Account 7',
  'Account 8',
  'Account 9',
  'Account 10',
] as const;
export type SharedZoomAccount = (typeof SHARED_ZOOM_ACCOUNTS)[number];

/** Fixed labels for the non-shared option kinds. */
export const PERSONAL_ZOOM_ACCOUNT = 'Personal' as const;
export const OTHER_ZOOM_ACCOUNT = 'Other' as const;

/** All canonical option values; selectors filter shared accounts by activation. */
export const ZOOM_ACCOUNT_OPTIONS = [
  ...SHARED_ZOOM_ACCOUNTS,
  PERSONAL_ZOOM_ACCOUNT,
  OTHER_ZOOM_ACCOUNT,
] as const;
export type ZoomAccountOption = (typeof ZOOM_ACCOUNT_OPTIONS)[number];

export type ParsedZoomAccount =
  | { kind: 'unassigned' }
  | { kind: 'shared'; account: SharedZoomAccount }
  | { kind: 'personal' }
  | { kind: 'other'; customValue: string };

const SHARED_ACCOUNT_LOOKUP: ReadonlyMap<string, SharedZoomAccount> = new Map(
  SHARED_ZOOM_ACCOUNTS.map(name => [normalizeForLookup(name), name])
);

const PERSONAL_LOOKUP_KEY = normalizeForLookup(PERSONAL_ZOOM_ACCOUNT);

function normalizeForLookup(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Parse the raw `notes` string into a structured kind.
 *
 * Accepts:
 *   null / undefined / empty / whitespace-only  → 'unassigned'
 *   case-insensitive match to a SHARED_ZOOM_ACCOUNTS entry  → 'shared'
 *   case-insensitive match to "Personal"        → 'personal'
 *   anything else (legacy free-form values)     → 'other' (original text preserved)
 */
export function parseZoomAccount(
  notes: string | null | undefined
): ParsedZoomAccount {
  if (notes == null) {
    return { kind: 'unassigned' };
  }
  if (typeof notes !== 'string') {
    return { kind: 'other', customValue: String(notes) };
  }

  const trimmed = notes.trim();
  if (trimmed === '') {
    return { kind: 'unassigned' };
  }

  const lookupKey = normalizeForLookup(trimmed);

  const sharedHit = SHARED_ACCOUNT_LOOKUP.get(lookupKey);
  if (sharedHit !== undefined) {
    return { kind: 'shared', account: sharedHit };
  }

  if (lookupKey === PERSONAL_LOOKUP_KEY) {
    return { kind: 'personal' };
  }

  return { kind: 'other', customValue: trimmed };
}

/**
 * Serialize a parsed kind back to the storage string.
 *
 *   'unassigned'  → null
 *   'shared'      → canonical display name (e.g., "Account 1")
 *   'personal'    → "Personal"
 *   'other'       → the customValue as typed (empty string allowed; caller decides)
 */
export function serializeZoomAccount(parsed: ParsedZoomAccount): string | null {
  switch (parsed.kind) {
    case 'unassigned':
      return null;
    case 'shared':
      return parsed.account;
    case 'personal':
      return PERSONAL_ZOOM_ACCOUNT;
    case 'other':
      return parsed.customValue;
  }
}

/** Type guard: true only for the 'shared' kind, which is conflict-checkable. */
export function isConflictCheckable(
  parsed: ParsedZoomAccount
): parsed is { kind: 'shared'; account: SharedZoomAccount } {
  return parsed.kind === 'shared';
}

/**
 * "Awaiting the teacher's own Zoom link": the account is Personal but no link
 * has been saved yet. This is a DERIVED state — deliberately not a stored
 * flag, so it can never go stale: the moment a real link is saved it clears
 * everywhere at once. It is what replaces the old workflow of admins pasting
 * a placeholder URL (which the system could not tell apart from a real link).
 *
 * Applies at both grains: a class default (ClassJoinCode.notes + zoomJoinUrl)
 * and a session override — its EFFECTIVE account and link, both after
 * fallback. A session on the class's link is on the class's account, so pass
 * `resolveSessionZoomLink`'s output rather than the override's raw fields.
 * Shared-account classes with a missing link are NOT this state — filling
 * those is the admin's job (Generate), not the teacher's.
 */
export function isAwaitingTeacherZoomLink(
  notes: string | null | undefined,
  zoomJoinUrl: string | null | undefined
): boolean {
  if (typeof zoomJoinUrl === 'string' && zoomJoinUrl.trim() !== '') {
    return false;
  }
  return parseZoomAccount(notes).kind === 'personal';
}

/**
 * Stable comparison key for grouping occurrences. Returns null for any kind
 * that is exempt from conflict checking — callers should skip those.
 */
export function zoomAccountConflictKey(
  parsed: ParsedZoomAccount
): string | null {
  if (parsed.kind !== 'shared') {
    return null;
  }
  return parsed.account;
}
